import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, apiError } from '@/lib/api';
import { useAuthStore } from '@/lib/auth';
import type { AuthResponse, User } from '@/types/api';

/**
 * Runs once on app mount: tries to silently refresh + fetch the current user.
 * - If we have a cached user but no token → hit /auth/refresh; on success hit /auth/me.
 * - If anything fails → leave the store empty, router will push /login.
 */
export function useAuthBootstrap(): void {
  const clear = useAuthStore((s) => s.clear);
  const setBootstrapping = useAuthStore((s) => s.setBootstrapping);
  const setUser = useAuthStore((s) => s.setUser);
  const user = useAuthStore((s) => s.user);
  const accessToken = useAuthStore((s) => s.accessToken);

  useEffect(() => {
    let cancelled = false;
    async function boot() {
      // If no cached user and no token, nothing to do — just let the guard show /login.
      if (!user && !accessToken) {
        setBootstrapping(false);
        return;
      }
      try {
        // If we don't have an access token, try to refresh using the httpOnly cookie.
        if (!accessToken) {
          const refreshRes = await api.post<{ accessToken: string }>('/auth/refresh');
          if (cancelled) return;
          const token = refreshRes.data?.accessToken;
          if (!token) throw new Error('no token');
          useAuthStore.getState().setAccessToken(token);
        }
        // Verify / refresh the user record.
        const meRes = await api.get<{ user: User }>('/auth/me');
        if (cancelled) return;
        setUser(meRes.data.user);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('auth bootstrap failed', apiError(err).message);
        if (!cancelled) clear();
      } finally {
        if (!cancelled) setBootstrapping(false);
      }
    }
    void boot();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

}

export function useLogin() {
  const { setSession } = useAuthStore();
  return async (email: string, password: string): Promise<void> => {
    const res = await api.post<AuthResponse>('/auth/login', { email, password });
    setSession(res.data.accessToken, res.data.user);
  };
}

export function useCurrentUser() {
  const user = useAuthStore((s) => s.user);
  return useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async () => (await api.get<{ user: User }>('/auth/me')).data.user,
    enabled: !!user,
    initialData: user ?? undefined,
    staleTime: 60_000,
  });
}
